<!--
	Select: a labelled native `<select>` over `{ value, label }` options, optionally grouped.
	Styled by the app.css form-control base.
-->
<script lang="ts" module>
	export type SelectOption = { value: string; label: string };
	export type SelectGroup = { label: string; options: SelectOption[] };
</script>

<script lang="ts">
	interface Props {
		options: (SelectOption | SelectGroup)[];
		/** Selected option `value`; `''` selects the placeholder. */
		value: string;
		label: string;
		/** Keeps the label for assistive tech only. */
		hideLabel?: boolean;
		/** Adds an empty first option with this text. */
		placeholder?: string;
		/** Label beside the control instead of above it. */
		inline?: boolean;
		/** sm is the compact 28px control, e.g. table pagination. */
		size?: 'sm' | 'md';
		disabled?: boolean;
		onchange?: (value: string) => void;
	}

	const {
		options,
		value,
		label,
		hideLabel = false,
		placeholder,
		inline = false,
		size = 'md',
		disabled = false,
		onchange
	}: Props = $props();

	const isGroup = (option: SelectOption | SelectGroup): option is SelectGroup =>
		'options' in option;
</script>

<label class="select {size}" class:inline>
	<span class:screenReaderOnly={hideLabel}>{label}</span>
	<select {value} {disabled} onchange={(e) => onchange?.(e.currentTarget.value)}>
		{#if placeholder !== undefined}
			<option value="">{placeholder}</option>
		{/if}
		{#each options as option, i (i)}
			{#if isGroup(option)}
				<optgroup label={option.label}>
					{#each option.options as item (item.value)}
						<option value={item.value}>{item.label}</option>
					{/each}
				</optgroup>
			{:else}
				<option value={option.value}>{option.label}</option>
			{/if}
		{/each}
	</select>
</label>

<style>
	.select {
		display: flex;
		flex-direction: column;
		gap: var(--space-1);

		&.inline {
			flex-direction: row;
			align-items: center;
			gap: var(--space-2);
		}

		select {
			min-width: 11rem;
		}

		&.sm {
			font-size: var(--font-size-sm);

			select {
				padding-block: 0;
				min-width: 0;
				min-height: var(--height-button-sm);
				font-size: inherit;
			}
		}
	}
</style>
